import { oneOf, optional } from "decoders";

import {
  LeadSelfAttestedType,
  QuestionSalesVolumeLeadForm,
} from "@brexhq/next/types/brex-api";

import { logMiscError } from "../../../telemetry/logging";

/**
 * Allows a mapping function to pass undefined values through as nulls for the gql api
 * Keeps mappers free from having to handle this case again and again
 */
export const mapUndefined = <T, U>(mapper: (input: U) => T) =>
  function inner(input: U | undefined) {
    if (input === undefined) return null;
    return mapper(input);
  };

export const mapSalesVolume = mapUndefined(
  optional(
    oneOf([
      QuestionSalesVolumeLeadForm.EQUAL_ABOVE_TWO_HUNDRED_FIFTY_K,
      QuestionSalesVolumeLeadForm.UNDER_TWO_HUNDRED_FIFTY_K,
    ]),
  ).verify,
);

export const mapSelfAttestedType = mapUndefined(
  optional(oneOf([LeadSelfAttestedType.ECOMMERCE, LeadSelfAttestedType.OTHER]))
    .verify,
);

export const mapMonthlySalesToMarketo = (sales?: string) => {
  try {
    const decoded = optional(
      oneOf([
        QuestionSalesVolumeLeadForm.EQUAL_ABOVE_TWO_HUNDRED_FIFTY_K,
        QuestionSalesVolumeLeadForm.UNDER_TWO_HUNDRED_FIFTY_K,
      ]),
    ).verify(sales);

    switch (decoded) {
      case QuestionSalesVolumeLeadForm.EQUAL_ABOVE_TWO_HUNDRED_FIFTY_K:
        return "$250K+";
      case QuestionSalesVolumeLeadForm.UNDER_TWO_HUNDRED_FIFTY_K:
        return "<$250K";
      case undefined:
        return null;
    }
  } catch (e) {
    if (e instanceof Error) logMiscError(e.message);
    return null;
  }
};

export const decodeIsEcomm = oneOf([
  LeadSelfAttestedType.ECOMMERCE,
  LeadSelfAttestedType.OTHER,
]);

export const mapIsEcommToMarketo = (isEcomm?: string) => {
  try {
    const decoded = optional(decodeIsEcomm).verify(isEcomm);

    switch (decoded) {
      case LeadSelfAttestedType.ECOMMERCE:
        return true;
      case LeadSelfAttestedType.OTHER:
        return false;
      case undefined:
        return null;
    }
  } catch (e) {
    if (e instanceof Error) logMiscError(e.message);
    return null;
  }
};
